import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./intro.css";

const INTRO_VIDEO =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/workplace_triumph/rules.mp4";
const NEXT_BUTTON_DELAY_MS = 3000;
// Safety net in case the video fails to load/play — matches old/rules.php's
// hard 7s cutoff so the player is never stuck on this screen.
const AUTO_ADVANCE_FALLBACK_MS = 7000;

function Intro() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const advancedRef = useRef(false);
  const [showNext, setShowNext] = useState(false);

  const goToStage1 = useCallback(() => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    navigate("/stage1", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const prevOverflow = document.documentElement.style.overflow;
    const prevBodyBackground = document.body.style.background;
    document.documentElement.style.overflow = "hidden";
    document.body.classList.remove("common-bg");
    // Any sliver not covered by the video (letterboxing, height-calc rounding)
    // must read as black, not the page's default white canvas.
    document.body.style.background = "#000";
    return () => {
      document.documentElement.style.overflow = prevOverflow;
      document.body.style.background = prevBodyBackground;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowNext(true), NEXT_BUTTON_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fallback = setTimeout(goToStage1, AUTO_ADVANCE_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [goToStage1]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;

    // iOS/WebKit can silently refuse autoplay unless `muted` is set as a real
    // DOM property before play() is requested (a JSX prop is sometimes too late).
    const tryPlay = () => {
      el.muted = true;
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    };
    el.defaultMuted = true;
    tryPlay();
    el.addEventListener("loadeddata", tryPlay);
    el.addEventListener("canplay", tryPlay);
    el.addEventListener("ended", goToStage1);
    return () => {
      el.removeEventListener("loadeddata", tryPlay);
      el.removeEventListener("canplay", tryPlay);
      el.removeEventListener("ended", goToStage1);
    };
  }, [goToStage1]);

  const handleNext = (e) => {
    e.preventDefault();
    goToStage1();
  };

  return (
    <div className="intro-page">
      <video
        ref={videoRef}
        className="intro-video"
        src={INTRO_VIDEO}
        autoPlay
        muted
        playsInline
        webkit-playsinline="true"
        preload="auto"
      />

      <div className="intro-next-wrap">
        <button
          type="button"
          className={`intro-next-btn${showNext ? " is-visible" : ""}`}
          onClick={handleNext}
          tabIndex={showNext ? 0 : -1}
          aria-hidden={!showNext}
        >
          <span>Next</span>
        </button>
      </div>
    </div>
  );
}

export default Intro;
